import { urqlClient } from '@shared/api';
import { SILENT_ERRORS_CONTEXT } from '@shared/api/silent-errors';

import { CANCEL_CONNECT, CONNECT_INTENT } from './graphql/mutations';
import { readIntentError } from './results';

// A Connect from a click (CC D16; ity-platform-backend docs/DEV-0000-crm-connectors.md "Front-end
// flow"). The sign-in tab opens synchronously in the click handler, before any await, so the
// browser does not block it; `connectIntent` runs only then, and its URL, which lives 90 s, is
// never asked for at render. The pending Connects live here, at module level, so a component that
// unmounts (a conversation switch, a closed panel) loses none of them.

/** How long a sign-in tab may read closed with no result before the Connect reads No answer. */
export const TAB_CLOSED_GRACE_MS = 5_000;

/** A pending Connect lapses after the CRM's longest attempt (`ATTEMPT_AT_PROVIDER_S`), CC D15. */
export const PENDING_LAPSE_MS = 660_000;

const TAB_WATCH_MS = 500;

/** `GOOGLE_DRIVE` for `google_drive`: GraphQL carries a service as its enum name. */
export function toGraphqlService(service: string): string {
  return service.toUpperCase();
}

export function fromGraphqlService(service: string): string {
  return service.toLowerCase();
}

let marked: Promise<void> | null = null;

/**
 * `POST /api/integrations/browser`: sets the `__Host-crm-connect` cookie `connectIntent` is
 * bound to. Once per page load; a failed call is made again on the next Connect.
 */
export function markBrowser(): Promise<void> {
  if (!marked) {
    const attempt: Promise<void> = fetch('/api/integrations/browser', {
      method: 'POST',
      credentials: 'include',
    }).then(
      (res) => {
        if (!res.ok && marked === attempt) marked = null;
      },
      () => {
        if (marked === attempt) marked = null;
      },
    );
    marked = attempt;
  }
  return marked;
}

/**
 * A blank tab for the sign-in, opened in the click handler. Null when the browser blocked it.
 * The tab is detached from this page (`opener`), which hears its end on the channel instead.
 */
export function openSignInTab(): Window | null {
  const tab = window.open('about:blank', '_blank');
  if (!tab) return null;
  tab.opener = null;
  return tab;
}

interface PendingConnect {
  service: string;
  tab: Window;
  startedAt: number;
  watch: ReturnType<typeof setInterval> | null;
  grace: ReturnType<typeof setTimeout> | null;
}

const pendingConnects = new Map<string, PendingConnect>();

/** What the flow tells the page: a sign-in tab read closed with no result for the grace. */
export interface FlowEvent {
  type: 'no_answer';
  service: string;
}

const listeners = new Set<(event: FlowEvent) => void>();

export function onFlowEvent(listener: (event: FlowEvent) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function stopTimers(entry: PendingConnect): void {
  if (entry.watch !== null) clearInterval(entry.watch);
  if (entry.grace !== null) clearTimeout(entry.grace);
  entry.watch = null;
  entry.grace = null;
}

/**
 * Watches the tab. One that reads closed with no result for the grace gets `cancelConnect` and a
 * `no_answer`, but stays pending: a provider's COOP can make an open tab read closed, and a
 * consent the user did finish still answers (CC D16). Past the Connect's lapse its attempt is over
 * at the CRM, and `cancelConnect`, which is per service, would end a newer one from another tab:
 * the lapsed Connect goes with nothing called, and still sends its `no_answer`.
 */
function register(service: string, tab: Window, startedAt: number): PendingConnect {
  const previous = pendingConnects.get(service);
  if (previous) stopTimers(previous);
  const entry: PendingConnect = { service, tab, startedAt, watch: null, grace: null };
  entry.watch = setInterval(() => {
    if (!entry.tab.closed) return;
    if (entry.watch !== null) clearInterval(entry.watch);
    entry.watch = null;
    entry.grace = setTimeout(() => {
      entry.grace = null;
      if (pendingConnects.get(service) !== entry) return;
      if (Date.now() - entry.startedAt > PENDING_LAPSE_MS) pendingConnects.delete(service);
      else void cancelConnect(service);
      for (const listener of listeners) listener({ type: 'no_answer', service });
    }, TAB_CLOSED_GRACE_MS);
  }, TAB_WATCH_MS);
  pendingConnects.set(service, entry);
  return entry;
}

function cancelConnect(service: string): Promise<unknown> {
  return urqlClient
    .mutation(CANCEL_CONNECT, { service: toGraphqlService(service) }, SILENT_ERRORS_CONTEXT)
    .toPromise();
}

/** How a Connect's start ended once `connectIntent` answered. */
export type ConnectOutcome = { status: 'started' } | { status: 'failed'; code: string };

/** A Connect's start: `blocked` when the browser blocked the tab, so nothing was called. */
export type ConnectStart =
  | { status: 'blocked' }
  | { status: 'opened'; startedAt: number; done: Promise<ConnectOutcome> };

/**
 * Starts a Connect from a click: opens the tab, marks the browser (not awaited before the tab
 * opens), records the pending Connect, then calls `connectIntent` with the upper-case service and
 * points the tab at its URL. On an error the tab closes and the pending Connect goes.
 */
export function beginConnect(service: string): ConnectStart {
  const tab = openSignInTab();
  if (!tab) return { status: 'blocked' };
  const browserMarked = markBrowser();
  const startedAt = Date.now();
  const entry = register(service, tab, startedAt);
  return { status: 'opened', startedAt, done: pointTab(entry, browserMarked) };
}

async function pointTab(
  entry: PendingConnect,
  browserMarked: Promise<void>,
): Promise<ConnectOutcome> {
  await browserMarked;
  const result = await urqlClient
    .mutation(CONNECT_INTENT, { service: toGraphqlService(entry.service) }, SILENT_ERRORS_CONTEXT)
    .toPromise();
  const url = result.data?.investbanq?.integrations?.connectIntent?.url;
  const current = pendingConnects.get(entry.service) === entry;
  if (result.error || !url) {
    entry.tab.close();
    if (current) {
      stopTimers(entry);
      pendingConnects.delete(entry.service);
    }
    return { status: 'failed', code: result.error ? readIntentError(result.error) : 'UNKNOWN' };
  }
  if (!current) {
    // Cancelled, or replaced by "open sign-in again", while `connectIntent` ran: this tab goes,
    // and a Cancel's attempt, which `connectIntent` has just begun, is ended again.
    entry.tab.close();
    if (!pendingConnects.has(entry.service)) void cancelConnect(entry.service);
    return { status: 'started' };
  }
  // A tab the user closed meanwhile is left to the watch: it reads No answer after the grace.
  if (!entry.tab.closed) entry.tab.location.href = url;
  return { status: 'started' };
}

/** Cancel: `cancelConnect`, and the pending Connect goes. A consent finished later still connects. */
export function cancel(service: string): Promise<unknown> {
  settle(service);
  return cancelConnect(service);
}

/** A Connect's result arrived: its pending Connect is done. */
export function settle(service: string): void {
  const entry = pendingConnects.get(service);
  if (!entry) return;
  stopTimers(entry);
  pendingConnects.delete(service);
}

/** The services with a Connect pending in this page. */
export function pendingServices(): string[] {
  return [...pendingConnects.keys()];
}
